import React from 'react';
import { Box, ToastProvider } from '@zillow/constellation-10';
import { AppLayoutContainer } from '@zg-rentals/zrm-header-navigation';
import { getPathWithPersistedQueryParams } from '@zg-rentals/string-utils';
import type { WebSocketWrapper } from '@zg-rentals/particles-js-websocket-wrapper';

import { useRentalManagerUser, useRmwPaths, useRouter, useStaticApp } from '../hooks';
import { Link as v4Link } from 'react-router-dom';
import { RequestWidget } from './RequestWidget';

import { type User } from '@zg-rentals/zrm-header-navigation';

export type AppContainerProps = {
  children: JSX.Element | Array<JSX.Element>;
  customerHeader?: JSX.Element;
  escapeAllLinks?: boolean | undefined;
  hideFooter?: boolean | undefined | null;
  hideNavigation?: boolean | undefined;
  isLoading?: boolean | undefined;
  userModel?: User | undefined;
  isInMobileWebView?: boolean;
  includeToastProvider?: boolean;
  forceAltLogo?: boolean;
};

declare global {
  interface Window {
    rentalManagerApiWebSocket: WebSocketWrapper;
  }
}

export const AppContainer = ({
  children,
  customerHeader,
  escapeAllLinks = true,
  hideFooter = false,
  hideNavigation = false,
  isLoading = false,
  userModel = undefined,
  isInMobileWebView = false,
  includeToastProvider = true,
  forceAltLogo,
}: AppContainerProps) => {
  const { user: userFromHook } = useRentalManagerUser();
  const { initialAuthComplete } = useStaticApp();

  const user = userModel || userFromHook;

  const { features } = user;
  const paths = useRmwPaths();
  const rentalManagerApiWebSocket = typeof window !== 'undefined' ? window.rentalManagerApiWebSocket : undefined;
  const router = useRouter();
  const pathname = router.location.pathname;
  const hasLoggedPageView = React.useRef(false);
  const includeSupportWidget = user.isMultifamilyCustomer || user.isSingleFamilyEnterpriseCustomer;

  const logoPathOverride = React.useMemo(
    () => getPathWithPersistedQueryParams(`${paths.zrmHomePage}/${router.location.search}`),
    [router.location.search],
  );

  // GA Tracking
  React.useEffect(() => {
    if (!initialAuthComplete || hasLoggedPageView.current) {
      return;
    }
    hasLoggedPageView.current = true;
  }, [initialAuthComplete, user]);

  React.useEffect(() => {
    // only track subsequent page changes
    if (!initialAuthComplete || !hasLoggedPageView.current) {
      return;
    }
  }, [router.location.pathname, initialAuthComplete]);

  const altLogo = forceAltLogo || (!forceAltLogo && !user.isEnterpriseCustomer);

  const content = (
    <Box css={{ backgroundColor: 'bg.softer', width: '100%', position: 'relative', height: '100%' }}>
      {customerHeader}
      <AppLayoutContainer
        isLoading={isLoading}
        hideNavigation={!!customerHeader || hideNavigation}
        hideFooter={!!hideFooter}
        altLogo={altLogo}
        brand="zillow"
        displayAnalytics={features?.canAccessMainAnalytics}
        escapeAllLinks={escapeAllLinks}
        logoPathOverride={logoPathOverride}
        pathname={pathname}
        reactRouterLink={v4Link}
        useRelativePaths={true}
        userModel={user}
        webSocketInstance={rentalManagerApiWebSocket}
        isInMobileWebView={isInMobileWebView}
      >
        {children}
        {includeSupportWidget && <RequestWidget />}
      </AppLayoutContainer>
    </Box>
  );

  return includeToastProvider ? <ToastProvider>{content}</ToastProvider> : content;
};
